import React, {ReactNode} from 'react';
import {Modal, theme, Typography} from 'antd';
import CloseIconDialog from 'Components/CloseIconDialog';
import {useCSContext} from 'Context/ContentStackContext';

interface IProps {
  children: ReactNode;
  closeDialogMethod: any;
  open: any;
  id?: any;
  style?: any;
  // Optional `.modal-header` (mockup pattern: title + close icon, divider
  // below) — omitted by every other DialogView call site today, which keeps
  // their existing floating-close-icon-only look unchanged.
  title?: string;
}

function DialogWrapper(props: IProps) {
  const {children, closeDialogMethod, open, title} = props;
  const {artifacts} = useCSContext();
  const {token}: any = theme.useToken();
  const isCompact = Boolean(props.style?.width);

  const handleClose = () => {
    closeDialogMethod(false);
  };

  return (
    <Modal
      // Unmount content on close, as the old MUI Dialog did, so each open starts fresh.
      destroyOnHidden
      open={open}
      onCancel={handleClose}
      // matches the original's disableEscapeKeyDown + "ignore backdrop click" behavior:
      // only the explicit close icon below can dismiss this dialog.
      closable={false}
      keyboard={false}
      maskClosable={false}
      footer={null}
      // antd's Modal is `position: relative` with `margin: 0 auto` (unlike the old MUI
      // `fullScreen` Dialog, which was `position: fixed; inset: 0`). Sizing this to
      // 100vw and then nudging it with a plain `left` offset — as a naive MUI->antd
      // port would — pushes an already-viewport-wide box off the right edge on every
      // screen size. Sizing to the remaining width and overriding the centering margin
      // instead reproduces the original "flush against the member-details sidebar,
      // spanning to the viewport's right edge" layout correctly. The offset must match
      // where Dashboard's main tab content actually starts (Pages/Dashboard/index.tsx):
      // MemberDetailsPanel's sidebar is a fixed 272px (the AST Console Revamp mockup's
      // sidebar width) plus its own root div's `margin: 0.75rem 10px` (MemberDetailsPanel/
      // index.tsx) — the flex row has no gap of its own, so real content begins at
      // 272 + 10px (sidebar's own right margin) + 10px (its left margin) = 292px. Using
      // bare 272px here would clip this dialog's leftmost content under the sidebar's margin.
      // A caller-provided width (e.g. FlowTypeConfirmationPopup's 40%,
      // CinemaECodePopup's 50%) means a small centred popup, as in the old MUI
      // dialog — antd's `width` prop would otherwise override `style.width` and
      // open it as a full-screen sheet.
      width={isCompact ? props.style.width : 'calc(100vw - 292px)'}
      style={
        isCompact
          ? {top: props.style.top ?? 100, paddingBottom: 0}
          : {
              top: 0,
              margin: 0,
              marginLeft: 292,
              maxWidth: 'none',
              paddingBottom: 0,
              ...props.style,
            }
      }
      // antd's default `.ant-modal-container` padding is 16px top/bottom (paddingMD),
      // so the body below is sized to 100vh minus that, keeping the dialog's total
      // height exactly 100vh — otherwise the outer modal wrapper also becomes
      // scrollable on top of the body's own scroll, a confusing double-scroll that
      // hides/clips content. (Left as the default padding otherwise, so content isn't
      // flush against the dialog's own edges.)
      styles={{
        body: isCompact
          ? {minHeight: props.style.height, overflow: 'auto', position: 'relative'}
          : {height: 'calc(100vh - 32px)', overflow: 'auto', position: 'relative'},
      }}>
      {title ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 60px 18px 24px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}>
          <Typography.Title level={4} style={{margin: 0}}>
            {title}
          </Typography.Title>
        </div>
      ) : null}
      <CloseIconDialog callback={handleClose} image={artifacts.cross_icon} />
      {children}
    </Modal>
  );
}

export default DialogWrapper;
